import decode from "jwt-decode";
import { getAuthToken } from "tap/utils/localStorageService.js";

/**
 * Merges user option lists and dismissBy users from exception rows.
 * @param {Array<{label: string, value: number|string}>} existingOptions
 * @param {Array<{label: string, value: number|string}>} apiOptions
 * @param {Array<Object>} exceptions
 * @returns {Array<{label: string, value: number|string}>}
 */
export function mergeDismissByUserOptions(
  existingOptions = [],
  apiOptions = [],
  exceptions = [],
) {
  const byId = new Map();

  for (const option of [...existingOptions, ...apiOptions]) {
    if (option?.value == null) continue;
    byId.set(option.value, option);
  }

  for (const row of exceptions) {
    const dismissBy = row?.dismissBy;
    if (dismissBy?.id == null) continue;

    byId.set(dismissBy.id, {
      label: dismissBy.fullName ?? String(dismissBy.id),
      value: dismissBy.id,
    });
  }

  return Array.from(byId.values()).sort((a, b) =>
    (a.label ?? "").localeCompare(b.label ?? ""),
  );
}

/**
 * Returns true when a dismiss API response represents an error payload.
 * The backend returns bare boolean `true` on success.
 * @param {*} response
 * @returns {boolean}
 */
export function isDismissApiError(response) {
  if (response == null || typeof response !== "object") {
    return false;
  }

  return Boolean(response.code || response.httpCode || response.error);
}

/**
 * Builds an exception-shaped row for mergeDismissByUserOptions from the JWT.
 * Avoids a /users/me round trip after dismiss.
 * @returns {Object|null}
 */
export function buildDismissByRowFromSession() {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const decoded = decode(token);
    const rawId = decoded?.id ?? decoded?.sub;
    if (rawId == null || rawId === "") return null;

    const id = Number(rawId);
    if (!Number.isFinite(id)) return null;

    const fullName = [
      localStorage.getItem("firstname"),
      localStorage.getItem("lastname"),
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      dismissBy: {
        id,
        fullName: fullName || decoded?.email || String(id),
      },
    };
  } catch (error) {
    console.error("Failed to decode session user from token:", error);
    return null;
  }
}
